import React, { useEffect, useMemo, useState } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { Link } from 'react-router-dom';
import { FiPlus, FiTrash2, FiAlertTriangle } from 'react-icons/fi';
import { MyModal } from '@shared/ui/my-modal';
import { Button } from '@shared/ui/button';
import { Select, SelectOption } from '@shared/ui/select';
import { useAppDispatch, useAppSelector } from '../../../app/store/store';
import { selectUser } from '../../../entities/auth/model';
import { fetchAllCompanies, selectCompanyList } from '../../../entities/company/model';
import { setErrorNotification, setNotification } from '../../../app/store/notifications';
import { asList, diagnosticsApi } from '../../../api/services/diagnostics.service';
import { canCreateRemote, isDiagnosticsStaff, typeByKind } from '@entities/diagnostics/lib';
import type {
  DeviceIntakePayload,
  DeviceModel,
  DiagnosticKind,
  DiagnosticTicket,
  DiagnosticTypeInfo,
  FaultReason,
  SerialDuplicate,
} from '../../../types/diagnosticsInterfaces';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  onCreated: (ticket: DiagnosticTicket) => void;
}

interface DeviceRow {
  manualModel: boolean;
  model: SelectOption | null;
  custom_model_name: string;
  serial_number: string;
  manualReason: boolean;
  fault_reason: SelectOption | null;
  fault_reason_text: string;
  client_reason: string;
}

interface FormValues {
  subject: string;
  kind: DiagnosticKind;
  company: SelectOption | null;
  sender_email: string;
  devices: DeviceRow[];
}

const emptyRow = (): DeviceRow => ({
  manualModel: false,
  model: null,
  custom_model_name: '',
  serial_number: '',
  manualReason: false,
  fault_reason: null,
  fault_reason_text: '',
  client_reason: '',
});

const FIELD_LABEL = 'block text-xs font-medium text-gray-500 mb-1';
const INPUT =
  'w-full h-[40px] px-3 rounded-lg border border-gray-200 text-sm text-[#2d2c2c] focus:outline-none focus:border-gray-400';
const selectStyles = { control: () => ({ height: '40px', minHeight: '40px' }) };

const errText = (e: any): string => {
  const data = e?.response?.data;
  if (!data) return 'Не удалось создать обращение';
  if (typeof data === 'string') return data;
  if (data.detail) return String(data.detail);
  // DRF: {field: [..]} или {devices: [{field: [..]}]}
  const flat = (v: any): string[] =>
    Array.isArray(v)
      ? v.flatMap(flat)
      : typeof v === 'object' && v
      ? Object.values(v).flatMap(flat)
      : [String(v)];
  return flat(data).filter(Boolean).slice(0, 3).join('; ') || 'Ошибка валидации';
};

export function DiagnosticTicketCreateModal({ isOpen, onClose, onCreated }: Props) {
  const dispatch = useAppDispatch();
  const me = useAppSelector(selectUser);
  const companies = useAppSelector(selectCompanyList) as { id: number; name: string }[];
  const isStaff = isDiagnosticsStaff(me);
  const remoteAllowed = canCreateRemote(me);

  const [types, setTypes] = useState<DiagnosticTypeInfo[]>([]);
  const [models, setModels] = useState<DeviceModel[]>([]);
  const [reasons, setReasons] = useState<FaultReason[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState('');
  // дубликаты по серийнику — по индексу строки
  const [duplicates, setDuplicates] = useState<Record<number, SerialDuplicate[]>>({});

  const {
    control, register, handleSubmit, watch, setValue, reset,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues: {
      subject: '',
      kind: 'onsite',
      company: null,
      sender_email: '',
      devices: [emptyRow()],
    },
  });
  const { fields, append, remove } = useFieldArray({ control, name: 'devices' });
  const rows = watch('devices');
  const kind = watch('kind');

  useEffect(() => {
    if (!isOpen) return;
    reset({ subject: '', kind: 'onsite', company: null, sender_email: '', devices: [emptyRow()] });
    setDuplicates({});
    setFormError('');
    diagnosticsApi.getTypes().then((r) => setTypes(r.data)).catch(() => setTypes([]));
    diagnosticsApi.getDeviceModels().then((r) => setModels(asList(r.data))).catch(() => setModels([]));
    diagnosticsApi.getFaultReasons().then((r) => setReasons(asList(r.data))).catch(() => setReasons([]));
    if (isStaff) dispatch(fetchAllCompanies());
  }, [isOpen, isStaff, dispatch, reset]);

  const modelOptions: SelectOption[] = useMemo(
    () => models.map((m) => ({ value: m.id, label: m.name })),
    [models],
  );
  const reasonOptions: SelectOption[] = useMemo(
    () => reasons.map((r) => ({ value: r.id, label: r.text })),
    [reasons],
  );
  const companyOptions: SelectOption[] = useMemo(
    () =>
      (companies ?? [])
        .map((c) => ({ value: c.id, label: c.name }))
        .sort((a, b) => a.label.localeCompare(b.label, 'ru')),
    [companies],
  );

  const checkSerial = async (index: number, serial: string) => {
    if (!isStaff || !serial.trim()) {
      setDuplicates((prev) => ({ ...prev, [index]: [] }));
      return;
    }
    try {
      const { data } = await diagnosticsApi.lookupSerial(serial.trim());
      setDuplicates((prev) => ({ ...prev, [index]: data.duplicates }));
    } catch {
      setDuplicates((prev) => ({ ...prev, [index]: [] }));
    }
  };

  const onSubmit = handleSubmit(async (values) => {
    setFormError('');
    const type = typeByKind(types, values.kind);
    if (!type) {
      setFormError('Тип обращения не настроен на сервере (нет диагностических типов).');
      return;
    }
    const devices: DeviceIntakePayload[] = [];
    for (let i = 0; i < values.devices.length; i++) {
      const r = values.devices[i];
      const modelName = r.custom_model_name.trim();
      if (r.manualModel ? !modelName : !r.model) {
        setFormError(`Строка ${i + 1}: укажите модель устройства.`);
        return;
      }
      const d: DeviceIntakePayload = {
        serial_number: r.serial_number.trim() || null,
        client_reason: r.client_reason.trim(),
      };
      if (r.manualModel) d.custom_model_name = modelName;
      else d.model = Number(r.model!.value);
      if (r.manualReason) {
        if (r.fault_reason_text.trim()) d.fault_reason_text = r.fault_reason_text.trim();
      } else if (r.fault_reason) {
        d.fault_reason = Number(r.fault_reason.value);
      }
      devices.push(d);
    }
    if (!devices.length) {
      setFormError('Добавьте хотя бы одно устройство.');
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await diagnosticsApi.createTicket({
        subject: values.subject.trim(),
        ticket_type: type.ticket_type.id,
        // для клиента сервер игнорирует company/sender_email — подставляет его компанию
        company: isStaff && values.company ? Number(values.company.value) : undefined,
        sender_email: isStaff ? values.sender_email.trim() : undefined,
        devices,
      });
      dispatch(setNotification(`Обращение ${data.number} создано`));
      onCreated(data);
    } catch (e) {
      const msg = errText(e);
      setFormError(msg);
      dispatch(setErrorNotification(msg));
    } finally {
      setSubmitting(false);
    }
  });

  return (
    <MyModal
      isOpen={isOpen}
      onClose={onClose}
      title="Новое обращение на диагностику"
      description={
        kind === 'remote'
          ? 'Дистанционная диагностика: клиент не уведомляется, пока вы не включите оповещения в карточке.'
          : 'Перечислите принятое оборудование. Каждое устройство — отдельной строкой.'
      }
      size="xl"
    >
      <form onSubmit={onSubmit} className="flex flex-col gap-4">
        {/* ── шапка ── */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div className="md:col-span-2">
            <label className={FIELD_LABEL}>Тема</label>
            <input
              className={`${INPUT} ${errors.subject ? 'border-red-400' : ''}`}
              placeholder="Напр.: Диагностика партии ДУТ"
              {...register('subject', { required: 'Укажите тему' })}
            />
            {errors.subject && <p className="mt-1 text-xs text-red-600">{errors.subject.message}</p>}
          </div>
          {isStaff && remoteAllowed && (
            <div>
              <label className={FIELD_LABEL}>Тип</label>
              <Controller
                control={control}
                name="kind"
                render={({ field }) => (
                  <Select
                    isVisible={false}
                    isClearable={false}
                    customStyles={selectStyles}
                    options={[
                      { value: 'onsite', label: 'Диагностика (приём оборудования)' },
                      { value: 'remote', label: 'Дистанционная диагностика' },
                    ]}
                    optionSelected={
                      field.value === 'remote'
                        ? { value: 'remote', label: 'Дистанционная диагностика' }
                        : { value: 'onsite', label: 'Диагностика (приём оборудования)' }
                    }
                    onSelected={(o) => field.onChange((o?.value as DiagnosticKind) ?? 'onsite')}
                  />
                )}
              />
            </div>
          )}
        </div>

        {isStaff && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <div>
              <label className={FIELD_LABEL}>Компания</label>
              <Controller
                control={control}
                name="company"
                render={({ field }) => (
                  <Select
                    isVisible={false}
                    placeholder="Выберите компанию"
                    customStyles={selectStyles}
                    options={companyOptions}
                    optionSelected={field.value}
                    onSelected={(o) => field.onChange(o)}
                  />
                )}
              />
            </div>
            <div>
              <label className={FIELD_LABEL}>Email контакта (если клиент без ЛК)</label>
              <input
                type="email"
                className={INPUT}
                placeholder="client@company.ru"
                {...register('sender_email')}
              />
            </div>
          </div>
        )}

        {/* ── устройства ── */}
        <div className="border border-gray-200 rounded-xl overflow-hidden">
          <div className="grid grid-cols-12 gap-2 px-3 py-2 bg-gray-50 text-xs font-medium text-gray-500">
            <div className="col-span-4">Модель</div>
            <div className="col-span-2">Серийный номер</div>
            <div className="col-span-3">Причина отправки</div>
            <div className="col-span-2">Примечание</div>
            <div className="col-span-1" />
          </div>

          {fields.map((f, index) => {
            const row = rows?.[index] ?? emptyRow();
            const dups = duplicates[index] ?? [];
            return (
              <div key={f.id} className="border-t border-gray-100 px-3 py-2">
                <div className="grid grid-cols-12 gap-2 items-start">
                  {/* модель */}
                  <div className="col-span-4">
                    {row.manualModel ? (
                      <input
                        className={INPUT}
                        placeholder="Название модели"
                        {...register(`devices.${index}.custom_model_name` as const)}
                      />
                    ) : (
                      <Controller
                        control={control}
                        name={`devices.${index}.model` as const}
                        render={({ field }) => (
                          <Select
                            isVisible={false}
                            placeholder="Выберите модель"
                            customStyles={selectStyles}
                            options={modelOptions}
                            optionSelected={field.value}
                            onSelected={(o) => field.onChange(o)}
                          />
                        )}
                      />
                    )}
                    <button
                      type="button"
                      className="mt-1 text-xs text-blue-600 hover:underline"
                      onClick={() => {
                        setValue(`devices.${index}.manualModel`, !row.manualModel);
                        setValue(`devices.${index}.model`, null);
                        setValue(`devices.${index}.custom_model_name`, '');
                      }}
                    >
                      {row.manualModel ? 'Выбрать из списка' : 'Указать вручную'}
                    </button>
                  </div>

                  {/* серийник */}
                  <div className="col-span-2">
                    <input
                      className={INPUT}
                      placeholder="б/н"
                      {...register(`devices.${index}.serial_number` as const, {
                        onBlur: (e) => checkSerial(index, e.target.value),
                      })}
                    />
                  </div>

                  {/* причина */}
                  <div className="col-span-3">
                    {row.manualReason ? (
                      <input
                        className={INPUT}
                        placeholder="Опишите причину"
                        {...register(`devices.${index}.fault_reason_text` as const)}
                      />
                    ) : (
                      <Controller
                        control={control}
                        name={`devices.${index}.fault_reason` as const}
                        render={({ field }) => (
                          <Select
                            isVisible={false}
                            placeholder="Выберите причину"
                            customStyles={selectStyles}
                            options={reasonOptions}
                            optionSelected={field.value}
                            onSelected={(o) => field.onChange(o)}
                          />
                        )}
                      />
                    )}
                    <button
                      type="button"
                      className="mt-1 text-xs text-blue-600 hover:underline"
                      onClick={() => {
                        setValue(`devices.${index}.manualReason`, !row.manualReason);
                        setValue(`devices.${index}.fault_reason`, null);
                        setValue(`devices.${index}.fault_reason_text`, '');
                      }}
                    >
                      {row.manualReason ? 'Выбрать из списка' : 'Указать вручную'}
                    </button>
                  </div>

                  {/* примечание */}
                  <div className="col-span-2">
                    <input
                      className={INPUT}
                      placeholder="—"
                      {...register(`devices.${index}.client_reason` as const)}
                    />
                  </div>

                  {/* удалить */}
                  <div className="col-span-1 flex justify-end">
                    <button
                      type="button"
                      title="Удалить строку"
                      disabled={fields.length === 1}
                      onClick={() => remove(index)}
                      className="h-[40px] w-[40px] inline-flex items-center justify-center rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50 disabled:opacity-30 disabled:cursor-not-allowed"
                    >
                      <FiTrash2 />
                    </button>
                  </div>
                </div>

                {/* дедуп: предупреждение + ссылка */}
                {dups.length > 0 && (
                  <div className="mt-2 flex items-start gap-2 rounded-lg bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800">
                    <FiAlertTriangle className="mt-0.5 shrink-0" />
                    <div>
                      Такой серийный номер уже встречался:{' '}
                      {dups.slice(0, 3).map((d, i) => (
                        <React.Fragment key={d.device_id}>
                          {i > 0 && ', '}
                          <Link
                            to={`/devices/diagnostics/${d.ticket_id}`}
                            className="underline font-medium"
                            target="_blank"
                            rel="noreferrer"
                          >
                            {d.ticket_number ?? `#${d.ticket_id}`}
                          </Link>
                          {d.ticket_type ? ` (${d.ticket_type})` : ''}
                        </React.Fragment>
                      ))}
                      {dups.length > 3 ? ` и ещё ${dups.length - 3}` : ''}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          <div className="border-t border-gray-100 px-3 py-2">
            <button
              type="button"
              onClick={() => append(emptyRow())}
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              <FiPlus /> Добавить устройство
            </button>
          </div>
        </div>

        {formError && <p className="text-sm text-red-600">{formError}</p>}

        <div className="flex items-center justify-end gap-2 pt-1">
          <Button isWhite type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="submit" disabled={submitting}>
            {submitting ? 'Создание…' : 'Создать обращение'}
          </Button>
        </div>
      </form>
    </MyModal>
  );
}
